import React, {useMemo, useCallback, useState} from "react";
import { useTable, useFlexLayout, useSortBy, useFilters, useExpanded } from "react-table";
import {FixedSizeList} from "react-window";
import {Icon, ResizeSensor} from "@blueprintjs/core";
import moment from "moment";
import {useTranslation} from "react-i18next";

// Libs
import scrollbarWidth from "../libs/scrollbarWidth";
import {formatNumber} from "../libs/numbers-lib";

import { grey } from "material-colors";
import useResizeSensorHandler from "../hooks/use-resize-sensor-handler";

const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => {
    //i18n
    const {t} = useTranslation("common");

    return (
        <input
            value={filterValue || ""}
            onChange={e => setFilter(e.target.value || undefined)}
            onClick={e => e.stopPropagation()}
            placeholder={t("actions.search")}
            style={{
                width: '100%',
                padding: '5px',
                height: 30
            }}
        />
    );
};

const VirtualizedTable = (props) => {
    const { columns, data, config } = props;
    const { sortable, showFooter, initialState, conditionalRowStyle } = config || {};
    const [resizeSensorEntries, setResizeSensorEntries] = useState(null);
    const defaultColumn = useMemo(() => ({
        minWidth: 150,
        Filter: DefaultColumnFilter // Default filter UI
    }), []);
    const scrollBarSize = useMemo(() => scrollbarWidth(), []);
    const rowGroupSize = useResizeSensorHandler(resizeSensorEntries);

    // Get table size
    const tableWidth = (rowGroupSize && rowGroupSize.width) || 0;
    const tableHeight = (rowGroupSize && rowGroupSize.height) || 0;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        rows,
        prepareRow,
    } = useTable({
        columns,
        data,
        defaultColumn,
        initialState,
        disableSortBy: !sortable,
        getSubRows: row => row.subRows,
        autoResetExpanded: false,
    },
        useFlexLayout,
        useFilters,
        useSortBy,
        useExpanded
    );

    const RenderRow = useCallback(({ index, style, isScrolling }) => {
        const row = rows[index];
        prepareRow(row);
        let conditionalStyle = {};
        if (conditionalRowStyle) {
            if (row?.original?.[conditionalRowStyle.dataField] === conditionalRowStyle.value) {
                conditionalStyle = conditionalRowStyle.style;
            }
        }

        return (
            <Tr {...row.getRowProps({
                style: {
                    ...style,
                    borderBottom: `1px solid ${grey[300]}`,
                    transition: "all 0.4s",
                    ...conditionalStyle
                },
                css: {
                    "&:hover": {
                        backgroundColor: grey[100]
                    }
                }
            })}>
                {row.cells.map(cell => {
                    const cellRender = (cell) => {
                        const type = cell.column.type;
                        switch (type) {
                            case "date":
                                return cell.render(({ value }) => {
                                    return value ? moment(value).format("DD/MM/YYYY") : null;
                                });
                            case "datetime":
                                return cell.render(({ value }) => {
                                    return moment(value).format("DD/MM/YYYY kk:mm:ss");
                                });
                            case "number":
                                return cell.render(({ value, column }) => {
                                    return formatNumber(value, column.precision);
                                });
                            case "function":
                                return cell.render(({ value, column }) => {
                                    return column.nodeContent(value);
                                });
                            default: return cell.render("Cell");
                        }
                    }

                    return (
                        <Td {...cell.getCellProps({
                            isNumber: cell.column.type === "number",
                            style: {
                                ...cell.column.style
                            }
                        })}>
                            {isScrolling ? "..." : cellRender(cell)}
                        </Td>
                    )
                })}
            </Tr>
        );

    }, [prepareRow, rows]);

    if (!data || data.length <= 0) return null;

    return (
        <Table {...getTableProps()}>
            {/* Header */}
            <Thead>
                {headerGroups.map(headerGroup => (
                    <Tr {...headerGroup.getHeaderGroupProps({
                        style: {
                            maxWidth: tableWidth - scrollBarSize
                        }
                    })}>
                        {headerGroup.headers.map(column => (
                            <Th {...column.getHeaderProps({
                                ...column.getSortByToggleProps(),
                                isNumber: column.type === "number",
                                style: {
                                    borderBottom: `1px solid ${grey[300]}`,
                                    cursor: column.disableSortBy ? undefined : "pointer",
                                    ...column.style
                                }
                            })}>
                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                    <div style={{ display: "flex" }}>
                                        <div>{column.render('Header')}</div>
                                        <div>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? <Icon style={{ marginLeft: 5 }} icon="chevron-down" />
                                                    : <Icon style={{ marginLeft: 5 }} icon="chevron-up" />
                                                : ''}
                                        </div>
                                    </div>
                                    <div>{column.filter ? column.render('Filter') : null}</div>
                                </div>
                            </Th>
                        ))}
                    </Tr>
                ))}
            </Thead>

            {/* Rows */}
            <div style={{ flex: 1, position: "relative" }}>
                <ResizeSensor onResize={setResizeSensorEntries}>
                    <Tbody {...getTableBodyProps()}>
                        <FixedSizeList
                            useIsScrolling
                            height={tableHeight}
                            itemCount={rows.length}
                            itemSize={38}
                            width={tableWidth}
                        >
                            {RenderRow}
                        </FixedSizeList>
                    </Tbody>
                </ResizeSensor>
            </div>

            {/* Footer */}
            <Tfoot showFooter={showFooter}>
                {footerGroups.map(group => (
                    <Tr {...group.getFooterGroupProps({ style: { maxWidth: tableWidth - scrollBarSize } })}>
                        {group.headers.map(column => (
                            <Th {...column.getFooterProps({
                                isNumber: column.type === "number",
                                style: { ...column.style }
                            })}>
                                {column.render((info) => {
                                    if (typeof column.Footer !== "string") {
                                        return "";
                                    }

                                    const { rows } = info;

                                    switch (column.Footer) {
                                        case "count":
                                            return rows.length;
                                        case "sum":
                                            return sumColumn(rows, column.id);
                                        default: return column.Footer;
                                    }

                                })}
                            </Th>
                        ))}
                    </Tr>
                ))}
            </Tfoot>
        </Table>
    );
};

export default VirtualizedTable;

const Table = ({ children, ...tableProps }) => (
    <div
        {...tableProps}
        style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            fontWeight: 100
        }}
    >
        {children}
    </div>
);

const Thead = ({ children }) => (
    <div>
        {children}
    </div>
);

const Tbody = ({ children, ...tbodyProps }) => (
    <div
        {...tbodyProps}
        style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        }}
    >
        {children}
    </div>
);

const Tfoot = ({ children, showFooter }) => {
    if (!showFooter) return null;
    return (
        <div>
            {children}
        </div>
    );
};

const Tr = ({ children, ...trProps }) => (
    <div {...trProps}>
        {children}
    </div>
);

const Th = ({ children, style, isNumber, ...thProps }) => (
    <div
        {...thProps}
        style={{
            fontWeight: "bold",
            padding: 10,
            textAlign: isNumber ? "right" : "left",
            ...style
        }}
    >
        {children}
    </div>
);

const Td = ({ children, style, isNumber, ...tdProps }) => (
    <div
        {...tdProps}
        style={{
            padding: 10,
            textAlign: isNumber ? "right" : "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...style
        }}
    >
        {children}
    </div>
);

const sumColumn = (rows, columnId) => {
    return rows.reduce((sum, row) => {
        const value = row.values[columnId];
        return sum + (typeof value === "number" ? value : 0);
    }, 0);
};
